import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  cancelChatbotMessage,
  createChatbotChat,
  fetchChatbotPendingMessage,
  sendChatbotMessage,
  toOptimisticFile
} from "@/components/chatbot-api";

export const POLL_INTERVAL_MS = 3000;
// The server gives up once the chat's processing lease lapses. This is only a
// client-side backstop for a poll that never gets a verdict at all.
export const POLL_TIMEOUT_MS = 4 * 60 * 60 * 1000;

function pendingMessageId(clientRequestId) {
  return `pending-${clientRequestId}`;
}

/**
 * Owns the send-and-await-answer cycle shared by the floating widget and the
 * fullscreen page: optimistic user message, polling for the background answer,
 * and error state.
 *
 * Polling resumes from the server: whenever the chat reports processing: true
 * this starts waiting again, so navigating away and back mid-answer keeps
 * showing progress instead of looking idle.
 *
 * onChatChanged is called whenever the server returns chat attributes, so a
 * surface with a sidebar can keep it in sync.
 */
export default function useChatbotConversation({
  initialChat = null,
  initialMessages = [],
  onChatChanged
} = {}) {
  const [chat, setChat] = useState(initialChat);
  const [messages, setMessages] = useState(initialMessages);
  const [submitting, setSubmitting] = useState(false);
  const [polling, setPolling] = useState(false);
  const [error, setError] = useState(null);
  const [pendingRequestId, setPendingRequestId] = useState(null);
  const [pendingUserMessageId, setPendingUserMessageId] = useState(null);
  const [stopping, setStopping] = useState(false);

  const pollStartedAtRef = useRef(null);
  const conversationVersionRef = useRef(0);
  const chatId = chat?.id || null;
  const chatIsProcessing = Boolean(chat?.processing);

  // The spinner should be up from the moment we submit until the answer or a
  // failure lands, across the gap where the POST has returned but polling has
  // not started yet.
  const processing = submitting || polling || stopping;
  const canStop = polling && Boolean(pendingUserMessageId) && chat?.can_message !== false && !stopping;

  const applyChat = useCallback(
    (payloadChat) => {
      if (!payloadChat) return;

      setChat(payloadChat);
      if (onChatChanged) onChatChanged(payloadChat);
    },
    [onChatChanged]
  );

  const beginPolling = useCallback((clientRequestId = null, userMessageId = null) => {
    pollStartedAtRef.current = Date.now();
    setPendingRequestId(clientRequestId);
    setPendingUserMessageId(userMessageId);
    setPolling(true);
  }, []);

  const stopPolling = useCallback(() => {
    pollStartedAtRef.current = null;
    setPendingRequestId(null);
    setPendingUserMessageId(null);
    setPolling(false);
  }, []);

  // Replace the optimistic row with the persisted pair.
  const settle = useCallback(
    (payload, clientRequestId) => {
      setMessages((current) => {
        const withoutPending = current.filter((message) => message.id !== pendingMessageId(clientRequestId));
        const settled = [payload.user_message, payload.assistant_message].filter(Boolean);
        const settledIds = new Set(settled.map((message) => message.id));

        return [...withoutPending.filter((message) => !settledIds.has(message.id)), ...settled];
      });
      applyChat(payload.chat);
      stopPolling();
    },
    [applyChat, stopPolling]
  );

  const failPending = useCallback(
    (clientRequestId, message) => {
      setMessages((current) => current.filter((entry) => entry.id !== pendingMessageId(clientRequestId)));
      setError(message);
      stopPolling();
    },
    [stopPolling]
  );

  // Resume waiting for an answer this client did not start - a reload, or a
  // navigation back into a chat whose job is still running.
  useEffect(() => {
    if (!chatIsProcessing || polling || submitting || stopping || error) return;

    beginPolling(null);
  }, [chatIsProcessing, polling, submitting, stopping, error, beginPolling]);

  useEffect(() => {
    if (!polling || !chatId || stopping) return undefined;

    let cancelled = false;
    const version = conversationVersionRef.current;

    const poll = async () => {
      try {
        const payload = await fetchChatbotPendingMessage(chatId, pendingRequestId);
        if (cancelled || version !== conversationVersionRef.current) return;

        if (payload.status === "processing") {
          setPendingUserMessageId(payload.user_message?.id || null);
          if (Date.now() - (pollStartedAtRef.current || Date.now()) > POLL_TIMEOUT_MS) {
            failPending(pendingRequestId, "Svaret tog for lang tid. Prøv at sende beskeden igen.");
          }
          return;
        }

        if (payload.status === "failed" && !payload.assistant_message) {
          failPending(pendingRequestId, payload.error || "Svaret blev afbrudt. Prøv at sende beskeden igen.");
          return;
        }

        // A failed answer still arrives as a message so the user can read it.
        settle(payload, pendingRequestId);
      } catch (pollError) {
        if (cancelled || version !== conversationVersionRef.current) return;

        failPending(pendingRequestId, pollError.message);
      }
    };

    poll();
    const timer = window.setInterval(poll, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [polling, chatId, pendingRequestId, stopping, failPending, settle]);

  const stop = useCallback(async () => {
    if (!canStop || !chatId) return;

    const version = ++conversationVersionRef.current;
    setStopping(true);
    setError(null);
    try {
      const payload = await cancelChatbotMessage(chatId, pendingUserMessageId);
      if (version !== conversationVersionRef.current) return;

      settle(payload, pendingRequestId);
    } catch (stopError) {
      if (version !== conversationVersionRef.current) return;

      setError(stopError.message);
    } finally {
      if (version === conversationVersionRef.current) setStopping(false);
    }
  }, [canStop, chatId, pendingUserMessageId, pendingRequestId, settle]);

  const submit = useCallback(
    async ({ question, uploads, organizationFiles, clientRequestId, reasoningEffort, mode, title }) => {
      const trimmedQuestion = question.trim();
      if (!trimmedQuestion || processing || chat?.can_message === false) return false;

      setError(null);
      setSubmitting(true);

      const optimisticFiles = [
        ...uploads.map((file, index) => toOptimisticFile(file, index)),
        ...organizationFiles.map((file) => toOptimisticFile(file))
      ];
      setMessages((current) => [
        ...current,
        {
          id: pendingMessageId(clientRequestId),
          role: "user",
          content: trimmedQuestion,
          references: [],
          files: optimisticFiles,
          created_at: new Date().toISOString()
        }
      ]);

      try {
        let activeChat = chat;
        if (!activeChat) {
          activeChat = await createChatbotChat(title || trimmedQuestion);
          applyChat(activeChat);
        }

        const payload = await sendChatbotMessage(activeChat.id, {
          question: trimmedQuestion,
          uploads,
          organizationFiles,
          clientRequestId,
          reasoningEffort,
          mode
        });
        applyChat(payload.chat);

        if (payload.assistant_message) {
          settle(payload, clientRequestId);
          return true;
        }

        // Accepted - keep the optimistic row on screen and wait for the answer.
        setMessages((current) =>
          current.map((message) =>
            message.id === pendingMessageId(clientRequestId) && payload.user_message
              ? { ...payload.user_message, id: pendingMessageId(clientRequestId) }
              : message
          )
        );
        beginPolling(clientRequestId, payload.user_message?.id);
        return true;
      } catch (submitError) {
        failPending(clientRequestId, submitError.message);
        return false;
      } finally {
        setSubmitting(false);
      }
    },
    [processing, chat, applyChat, settle, failPending, beginPolling]
  );

  // Replaces the whole conversation, for a surface that navigated to a
  // different chat. Any in-flight poll for the previous chat is dropped.
  const openChat = useCallback(
    (nextChat, nextMessages = []) => {
      conversationVersionRef.current += 1;
      stopPolling();
      setStopping(false);
      setError(null);
      setChat(nextChat || null);
      setMessages(nextMessages);
    },
    [stopPolling]
  );

  return useMemo(
    () => ({
      chat,
      messages,
      setMessages,
      processing,
      canStop,
      stopping,
      stop,
      error,
      setError,
      submit,
      openChat
    }),
    [chat, messages, processing, canStop, stopping, stop, error, submit, openChat]
  );
}
